import {
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  PaperAirplaneIcon,
  PrinterIcon,
  TruckIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type {
  CarrierTrackingData,
  ReadPostgridContact,
} from '@/services/api/generated/postgridService';

import { AttachmentList } from '../postgrid-attachments/AttachmentList';
import { CancelButton } from './CancelButton';
import { JourneyMap } from './JourneyMap';
import { TestModeBanner } from './TestModeBanner';

// ═══════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════

interface ChequeSpecificProps {
  amount?: number | string;
  memo?: string | null;
  number?: number | null;
  dateCleared?: string | null;
  chequeType?: string;
  bankAccountId?: string | null;
}

interface LetterSpecificProps {
  subject?: string | null;
  content?: string | null;
  letterSize?: string;
}

export interface MailItemDetailsContentProps {
  entityType: 'letter' | 'cheque';
  status?: string;
  live?: boolean;
  mailingClass?: string;
  trackingNumber?: string | null;
  carrierTracking?: CarrierTrackingData | null;
  toContact?: ReadPostgridContact | null;
  fromContact?: ReadPostgridContact | null;
  dateSent?: string | null;
  dateDelivered?: string | null;
  expectedDeliveryDate?: string | null;
  finalPdfUrl?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attachments?: any[] | null;
  matterId?: string;
  returnEnvelopeId?: string | null;
  color?: boolean;
  doubleSided?: boolean;
  postgridId?: string;
  internalId?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  events?: any[];
  onCancel?: () => void;
  isCancelling?: boolean;
  cheque?: ChequeSpecificProps;
  letter?: LetterSpecificProps;
}

// ═══════════════════════════════════════════════════════════════════
// Status Config
// ═══════════════════════════════════════════════════════════════════

const STATUS_MAP = {
  delivered: {
    gradient: 'from-emerald-500 to-green-600',
    icon: CheckCircleSolidIcon,
    label: 'Delivered',
  },
  out_for_delivery: {
    gradient: 'from-cyan-500 to-teal-600',
    icon: TruckIcon,
    label: 'Out for Delivery',
  },
  in_transit: {
    gradient: 'from-blue-500 to-indigo-600',
    icon: TruckIcon,
    label: 'In Transit',
  },
  cancelled: {
    gradient: 'from-rose-500 to-red-600',
    icon: XCircleIcon,
    label: 'Cancelled',
  },
  failed: {
    gradient: 'from-rose-500 to-red-600',
    icon: XCircleIcon,
    label: 'Failed',
  },
  printing: {
    gradient: 'from-amber-500 to-orange-600',
    icon: PrinterIcon,
    label: 'Printing',
  },
  completed: {
    gradient: 'from-violet-500 to-purple-600',
    icon: PaperAirplaneIcon,
    label: 'Sent',
  },
  ready: {
    gradient: 'from-slate-400 to-slate-500',
    icon: ClockIcon,
    label: 'Ready',
  },
  default: {
    gradient: 'from-slate-400 to-slate-500',
    icon: ClockIcon,
    label: 'Preparing',
  },
} as const;

const getStatus = (status?: string, carrierStatus?: string) => {
  const s = (carrierStatus || status || '').toLowerCase();
  if (s.includes('delivered')) return STATUS_MAP.delivered;
  if (s.includes('out_for_delivery') || s.includes('out for delivery'))
    return STATUS_MAP.out_for_delivery;
  if (s.includes('transit') || s.includes('processed'))
    return STATUS_MAP.in_transit;
  if (s === 'cancelled') return STATUS_MAP.cancelled;
  if (s === 'failed') return STATUS_MAP.failed;
  if (s === 'printing') return STATUS_MAP.printing;
  if (s === 'completed') return STATUS_MAP.completed;
  if (s === 'ready') return STATUS_MAP.ready;
  return STATUS_MAP.default;
};

// ═══════════════════════════════════════════════════════════════════
// Formatters
// ═══════════════════════════════════════════════════════════════════

const fmt = {
  mail: (v?: string) =>
    ({
      first_class: 'First Class',
      certified: 'Certified',
      certified_return_receipt: 'Certified + RR',
      express: 'Express',
      priority: 'Priority',
    })[v || ''] ||
    v ||
    'Standard',

  currency: (v?: number | string) => {
    if (v == null) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(typeof v === 'string' ? parseFloat(v) : v);
  },

  date: (d?: string | null) => {
    if (!d) return null;
    try {
      return new Date(d).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return d;
    }
  },

  dateLong: (d?: string | null) => {
    if (!d) return null;
    try {
      return new Date(d).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return d;
    }
  },

  name: (c?: ReadPostgridContact | null) =>
    c?.company_name ||
    [c?.first_name, c?.last_name].filter(Boolean).join(' ') ||
    'Unknown',
};

// ═══════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════

export const MailItemDetailsContent = ({
  entityType,
  status,
  live,
  mailingClass,
  trackingNumber,
  carrierTracking,
  toContact,
  fromContact,
  dateSent,
  dateDelivered,
  expectedDeliveryDate,
  finalPdfUrl,
  attachments,
  matterId,
  returnEnvelopeId,
  color,
  doubleSided,
  postgridId,
  internalId,
  events = [],
  onCancel,
  isCancelling,
  cheque,
  letter,
}: MailItemDetailsContentProps) => {
  const cfg = getStatus(status, carrierTracking?.status);
  const Icon = cfg.icon;
  const isCheque = entityType === 'cheque';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const signedBy =
    carrierTracking?.signedBy || (carrierTracking as any)?.signed_by;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const expectedDate =
    expectedDeliveryDate ||
    carrierTracking?.estDeliveryDate ||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (carrierTracking as any)?.est_delivery_date;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const trackingUpdates =
    carrierTracking?.trackingUpdates ||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (carrierTracking as any)?.tracking_updates;

  const isDelivered = cfg.label === 'Delivered';
  const canCancel = status === 'creating' || status === 'ready';
  const showCancelOption =
    status !== 'cancelled' && status !== 'delivered' && status !== 'failed';

  const getCancelDisabledReason = () => {
    const statusLabels: Record<string, string> = {
      printing: 'printing',
      processed_for_delivery: 'processed for delivery',
      completed: 'sent to carrier',
      in_transit: 'in transit',
      in_local_area: 'in local area',
      out_for_delivery: 'out for delivery',
      re_routed: 're-routed',
      returned_to_sender: 'returned to sender',
      processed: 'processed',
    };
    const label = statusLabels[status || ''] || status || 'unknown';
    return `Cannot cancel \u2014 ${entityType} is already ${label}`;
  };
  const hasTracking = trackingUpdates?.length > 0;

  return (
    <div className="space-y-3">
      <TestModeBanner isTest={!live} />

      {/* ══════════ Hero Card ══════════ */}
      <div
        className={cn(
          'relative overflow-hidden rounded-2xl bg-gradient-to-br p-4 shadow-lg',
          cfg.gradient,
        )}
      >
        <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-white/10" />
        <div className="absolute -bottom-4 -left-4 h-16 w-16 rounded-full bg-white/5" />

        <div className="relative flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-white/20 p-2.5 backdrop-blur-sm">
              <Icon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">{cfg.label}</h2>
              {isDelivered && signedBy && (
                <p className="text-sm text-white/80">Signed by {signedBy}</p>
              )}
              {!isDelivered && expectedDate && cfg.label !== 'Preparing' && (
                <p className="text-sm text-white/80">
                  Expected {fmt.dateLong(expectedDate)}
                </p>
              )}
            </div>
          </div>

          {isCheque && cheque?.amount && (
            <div className="text-right">
              <div className="text-2xl font-bold text-white">
                {fmt.currency(cheque.amount)}
              </div>
              {cheque.number && (
                <div className="text-xs text-white/70">#{cheque.number}</div>
              )}
            </div>
          )}
        </div>

        {/* Tags - consolidated info */}
        <div className="relative mt-3 flex flex-wrap items-center gap-1.5">
          <Tag>{fmt.mail(mailingClass)}</Tag>
          {color && <Tag>Color</Tag>}
          {doubleSided && <Tag>2-Sided</Tag>}
          {returnEnvelopeId && <Tag>Return Env</Tag>}
          {dateSent && (
            <Tag>
              <PaperAirplaneIcon className="mr-1 h-3 w-3" />
              Sent {fmt.date(dateSent)}
            </Tag>
          )}
          {dateDelivered && (
            <Tag>
              <CheckCircleIcon className="mr-1 h-3 w-3" />
              {fmt.date(dateDelivered)}
            </Tag>
          )}
          {isCheque && cheque?.dateCleared && (
            <Tag>
              <CheckCircleIcon className="mr-1 h-3 w-3" />
              Cleared {fmt.date(cheque.dateCleared)}
            </Tag>
          )}
        </div>
      </div>

      {/* ══════════ Actions ══════════ */}
      <div className="flex flex-wrap gap-2">
        {finalPdfUrl && (
          <Button
            size="sm"
            className="h-8 gap-1.5 bg-slate-900 text-xs hover:bg-slate-800"
            onClick={() =>
              window.open(finalPdfUrl, '_blank', 'noopener,noreferrer')
            }
          >
            <DocumentTextIcon className="h-3.5 w-3.5" />
            View PDF
          </Button>
        )}
        {canCancel && onCancel && (
          <CancelButton
            onConfirm={onCancel}
            isLoading={isCancelling}
            entityType={entityType}
            entityName={
              isCheque
                ? `Check #${cheque?.number || 'N/A'}`
                : (letter?.subject ?? undefined)
            }
            entityId={postgridId}
            size="sm"
          />
        )}
        {!canCancel && showCancelOption && onCancel && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <CancelButton
                  onConfirm={onCancel}
                  disabled
                  entityType={entityType}
                  size="sm"
                />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {getCancelDisabledReason()}
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* ══════════ Journey Map (includes tracking link + copyable number) ══════════ */}
      {hasTracking && (
        <JourneyMap
          carrierTracking={carrierTracking}
          trackingNumber={trackingNumber}
        />
      )}

      {/* ══════════ Addresses ══════════ */}
      <div className="grid gap-2 sm:grid-cols-2">
        {fromContact && <AddressCard label="From" contact={fromContact} />}
        <AddressCard
          label={isCheque ? 'Payable To' : 'To'}
          contact={toContact}
          accent={isCheque ? 'emerald' : 'indigo'}
        />
      </div>

      {/* ══════════ Memo / Subject ══════════ */}
      {isCheque && cheque?.memo && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <p className="text-[9px] font-semibold uppercase text-slate-400">
            Memo
          </p>
          <p className="text-sm text-slate-700">{cheque.memo}</p>
        </div>
      )}
      {!isCheque && letter?.subject && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <p className="text-[9px] font-semibold uppercase text-slate-400">
            Subject
          </p>
          <p className="text-sm text-slate-700">{letter.subject}</p>
        </div>
      )}

      {/* ══════════ Letter Content ══════════ */}
      {!isCheque && letter?.content && (
        <Collapse title="Letter Content">
          <div
            className="prose prose-sm max-w-none text-slate-600"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: letter.content }}
          />
        </Collapse>
      )}

      {/* ══════════ Attachments ══════════ */}
      {attachments && attachments.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
          <div className="mb-2 flex items-center justify-between text-xs">
            <span className="font-medium text-slate-700">Attachments</span>
            <span className="text-slate-400">{attachments.length}</span>
          </div>
          <AttachmentList
            attachments={attachments}
            showAddButton={false}
            matterId={matterId}
          />
        </div>
      )}

      {/* ══════════ Technical ══════════ */}
      <details className="group">
        <summary className="flex cursor-pointer items-center gap-2 text-[11px] text-slate-400 hover:text-slate-500">
          <span>Technical</span>
          <span className="h-px flex-1 bg-slate-200" />
        </summary>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px]">
          {postgridId && (
            <span className="text-slate-400">
              PG: <code className="text-slate-500">{postgridId}</code>
            </span>
          )}
          {internalId && (
            <span className="text-slate-400">
              ID: <code className="text-slate-500">{internalId}</code>
            </span>
          )}
          {status && (
            <span className="text-slate-400">
              Status: <code className="text-slate-500">{status}</code>
            </span>
          )}
          {isCheque && cheque?.bankAccountId && (
            <span className="text-slate-400">
              Bank:{' '}
              <code className="text-slate-500">{cheque.bankAccountId}</code>
            </span>
          )}
        </div>
      </details>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Sub-components
// ═══════════════════════════════════════════════════════════════════

const Tag = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-flex items-center rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur-sm">
    {children}
  </span>
);

const AddressCard = ({
  label,
  contact,
  accent = 'slate',
}: {
  label: string;
  contact?: ReadPostgridContact | null;
  accent?: 'slate' | 'indigo' | 'emerald';
}) => {
  const accentColor = {
    slate: 'text-slate-400',
    indigo: 'text-indigo-500',
    emerald: 'text-emerald-500',
  }[accent];

  return (
    <div className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5">
      <p className={cn('text-[9px] font-semibold uppercase', accentColor)}>
        {label}
      </p>
      <p className="text-xs font-medium text-slate-900">{fmt.name(contact)}</p>
      <div className="text-[11px] leading-snug text-slate-500">
        <p>{contact?.address_line1}</p>
        {contact?.address_line2 && <p>{contact.address_line2}</p>}
        <p>
          {contact?.city}, {contact?.province_or_state} {contact?.postal_or_zip}
        </p>
      </div>
    </div>
  );
};

const Collapse = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <details className="group rounded-xl border border-slate-200 bg-white shadow-sm">
    <summary className="flex cursor-pointer items-center justify-between px-3 py-2 text-sm font-medium text-slate-900 hover:bg-slate-50/50">
      {title}
    </summary>
    <div className="border-t border-slate-200 px-3 py-2">{children}</div>
  </details>
);
